// LP (Liquidity Provider) Constants
// All enums with matching _LABELS maps for the FX Treasury Dealer interface

// ─── RFQ Types ──────────────────────────────────────────────────────────
export const RFQ_TYPES = Object.freeze({
  COMPETITIVE: 'competitive',
  SINGLE: 'single',
  STANDING: 'standing',
});

export const RFQ_TYPE_LABELS = Object.freeze({
  [RFQ_TYPES.COMPETITIVE]: 'Competitive',
  [RFQ_TYPES.SINGLE]: 'Single Dealer',
  [RFQ_TYPES.STANDING]: 'Standing Order',
});

// ─── RFQ Statuses ───────────────────────────────────────────────────────
export const RFQ_STATUSES = Object.freeze({
  PENDING: 'pending',
  LIVE: 'live',
  QUOTED: 'quoted',
  AWARDED: 'awarded',
  EXPIRED: 'expired',
  CANCELLED: 'cancelled',
  DECLINED: 'declined',
});

export const RFQ_STATUS_LABELS = Object.freeze({
  [RFQ_STATUSES.PENDING]: 'Pending',
  [RFQ_STATUSES.LIVE]: 'Live',
  [RFQ_STATUSES.QUOTED]: 'Quoted',
  [RFQ_STATUSES.AWARDED]: 'Awarded',
  [RFQ_STATUSES.EXPIRED]: 'Expired',
  [RFQ_STATUSES.CANCELLED]: 'Cancelled',
  [RFQ_STATUSES.DECLINED]: 'Declined',
});

// ─── Quote Statuses ─────────────────────────────────────────────────────
export const QUOTE_STATUSES = Object.freeze({
  LIVE: 'live',
  AMENDED: 'amended',
  WITHDRAWN: 'withdrawn',
  ACCEPTED: 'accepted',
  REJECTED: 'rejected',
  EXPIRED: 'expired',
});

export const QUOTE_STATUS_LABELS = Object.freeze({
  [QUOTE_STATUSES.LIVE]: 'Live',
  [QUOTE_STATUSES.AMENDED]: 'Amended',
  [QUOTE_STATUSES.WITHDRAWN]: 'Withdrawn',
  [QUOTE_STATUSES.ACCEPTED]: 'Accepted',
  [QUOTE_STATUSES.REJECTED]: 'Rejected',
  [QUOTE_STATUSES.EXPIRED]: 'Expired',
});

// ─── LP Trade Statuses ──────────────────────────────────────────────────
export const LP_TRADE_STATUSES = Object.freeze({
  BOOKED: 'booked',
  CONFIRMED: 'confirmed',
  INSTRUCTED: 'instructed',
  AWAITING_FUNDS: 'awaiting_funds',
  FUNDS_RECEIVED: 'funds_received',
  RELEASED: 'released',
  SETTLED: 'settled',
  CSD_PAYMENT_CONFIRMED: 'csd_payment_confirmed',
  CANCELLED: 'cancelled',
  FAILED: 'failed',
});

export const LP_TRADE_STATUS_LABELS = Object.freeze({
  [LP_TRADE_STATUSES.BOOKED]: 'Booked',
  [LP_TRADE_STATUSES.CONFIRMED]: 'Confirmed',
  [LP_TRADE_STATUSES.INSTRUCTED]: 'Instructed',
  [LP_TRADE_STATUSES.AWAITING_FUNDS]: 'Awaiting Funds',
  [LP_TRADE_STATUSES.FUNDS_RECEIVED]: 'Funds Received',
  [LP_TRADE_STATUSES.RELEASED]: 'Released',
  [LP_TRADE_STATUSES.SETTLED]: 'Settled',
  [LP_TRADE_STATUSES.CSD_PAYMENT_CONFIRMED]: 'CSD Payment Confirmed',
  [LP_TRADE_STATUSES.CANCELLED]: 'Cancelled',
  [LP_TRADE_STATUSES.FAILED]: 'Failed',
});

// ─── RFQ Purposes ───────────────────────────────────────────────────────
export const RFQ_PURPOSES = Object.freeze({
  INVESTMENT: 'investment',
  REPATRIATION: 'repatriation',
  DIVIDEND: 'dividend',
  CAPITAL_CALL: 'capital_call',
  REDEMPTION: 'redemption',
  FEE_PAYMENT: 'fee_payment',
});

export const RFQ_PURPOSE_LABELS = Object.freeze({
  [RFQ_PURPOSES.INVESTMENT]: 'Investment',
  [RFQ_PURPOSES.REPATRIATION]: 'Repatriation',
  [RFQ_PURPOSES.DIVIDEND]: 'Dividend',
  [RFQ_PURPOSES.CAPITAL_CALL]: 'Capital Call',
  [RFQ_PURPOSES.REDEMPTION]: 'Redemption',
  [RFQ_PURPOSES.FEE_PAYMENT]: 'Fee Payment',
});

// ─── Decline Reasons ────────────────────────────────────────────────────
export const DECLINE_REASONS = Object.freeze({
  OFF_POLICY: 'off_policy',
  COUNTERPARTY_LIMIT: 'counterparty_limit',
  PAIR_LIMIT: 'pair_limit',
  NOP_BREACH: 'nop_breach',
  STALE_PRICING: 'stale_pricing',
  KYC_INCOMPLETE: 'kyc_incomplete',
  SUSPENDED_COUNTERPARTY: 'suspended_counterparty',
  MISSING_DOCUMENTS: 'missing_documents',
  SIZE_TOO_LARGE: 'size_too_large',
  MANUAL: 'manual',
});

export const DECLINE_REASON_LABELS = Object.freeze({
  [DECLINE_REASONS.OFF_POLICY]: 'Off Policy',
  [DECLINE_REASONS.COUNTERPARTY_LIMIT]: 'Counterparty Limit Reached',
  [DECLINE_REASONS.PAIR_LIMIT]: 'Pair Limit Reached',
  [DECLINE_REASONS.NOP_BREACH]: 'NOP Breach',
  [DECLINE_REASONS.STALE_PRICING]: 'Stale Pricing',
  [DECLINE_REASONS.KYC_INCOMPLETE]: 'KYC Incomplete',
  [DECLINE_REASONS.SUSPENDED_COUNTERPARTY]: 'Counterparty Suspended',
  [DECLINE_REASONS.MISSING_DOCUMENTS]: 'Missing Documents',
  [DECLINE_REASONS.SIZE_TOO_LARGE]: 'Size Too Large',
  [DECLINE_REASONS.MANUAL]: 'Manual Decline',
});

// ─── LP Document Types ──────────────────────────────────────────────────
export const LP_DOC_TYPES = Object.freeze({
  ALLOCATION_NOTICE: 'allocation_notice',
  SOURCE_OF_FUNDS: 'source_of_funds',
  PURPOSE_CODE: 'purpose_code',
  CB_FORM: 'cb_form',
  BOARD_RESOLUTION: 'board_resolution',
  TRUST_DEED: 'trust_deed',
  POWER_OF_ATTORNEY: 'power_of_attorney',
  TAX_CERTIFICATE: 'tax_certificate',
});

export const LP_DOC_TYPE_LABELS = Object.freeze({
  [LP_DOC_TYPES.ALLOCATION_NOTICE]: 'Allocation Notice',
  [LP_DOC_TYPES.SOURCE_OF_FUNDS]: 'Source of Funds Declaration',
  [LP_DOC_TYPES.PURPOSE_CODE]: 'Purpose Code Certificate',
  [LP_DOC_TYPES.CB_FORM]: 'Central Bank Form',
  [LP_DOC_TYPES.BOARD_RESOLUTION]: 'Board Resolution',
  [LP_DOC_TYPES.TRUST_DEED]: 'Trust Deed',
  [LP_DOC_TYPES.POWER_OF_ATTORNEY]: 'Power of Attorney',
  [LP_DOC_TYPES.TAX_CERTIFICATE]: 'Tax Certificate',
});

// ─── Counterparty KYC Statuses ──────────────────────────────────────────
export const COUNTERPARTY_KYC_STATUSES = Object.freeze({
  VERIFIED: 'verified',
  PENDING: 'pending',
  EXPIRED: 'expired',
  SUSPENDED: 'suspended',
  PROHIBITED: 'prohibited',
  REVIEW_DUE: 'review_due',
});

export const COUNTERPARTY_KYC_STATUS_LABELS = Object.freeze({
  [COUNTERPARTY_KYC_STATUSES.VERIFIED]: 'Verified',
  [COUNTERPARTY_KYC_STATUSES.PENDING]: 'Pending',
  [COUNTERPARTY_KYC_STATUSES.EXPIRED]: 'Expired',
  [COUNTERPARTY_KYC_STATUSES.SUSPENDED]: 'Suspended',
  [COUNTERPARTY_KYC_STATUSES.PROHIBITED]: 'Prohibited',
  [COUNTERPARTY_KYC_STATUSES.REVIEW_DUE]: 'Review Due',
});

// ─── LP Roles ───────────────────────────────────────────────────────────
export const LP_ROLES = Object.freeze({
  DEALER: 'dealer',
  SENIOR_DEALER: 'senior_dealer',
  LP_OPERATIONS: 'lp_operations',
  LP_COMPLIANCE: 'lp_compliance',
  LP_AUDITOR: 'lp_auditor',
});

export const LP_ROLE_LABELS = Object.freeze({
  [LP_ROLES.DEALER]: 'Dealer',
  [LP_ROLES.SENIOR_DEALER]: 'Senior Dealer',
  [LP_ROLES.LP_OPERATIONS]: 'Operations',
  [LP_ROLES.LP_COMPLIANCE]: 'Compliance',
  [LP_ROLES.LP_AUDITOR]: 'Auditor',
});

// ─── Supported Currency Pairs ───────────────────────────────────────────
export const SUPPORTED_PAIRS = Object.freeze([
  'USD/KES', 'EUR/KES', 'GBP/KES',
  'USD/UGX', 'EUR/UGX', 'GBP/UGX',
  'USD/TZS', 'EUR/TZS', 'GBP/TZS',
  'USD/RWF', 'EUR/RWF',
  'USD/NGN', 'EUR/NGN',
  'USD/ZAR', 'EUR/ZAR', 'GBP/ZAR',
]);

export const SUPPORTED_PAIRS_SET = Object.freeze(new Set(SUPPORTED_PAIRS));

// ─── Settlement Steps ───────────────────────────────────────────────────
export const SETTLEMENT_STEPS = Object.freeze([
  { key: 'booked', label: 'Booked', order: 0 },
  { key: 'confirmed', label: 'Confirmed', order: 1 },
  { key: 'instructed', label: 'Instructed', order: 2 },
  { key: 'awaiting_funds', label: 'Awaiting Funds', order: 3 },
  { key: 'funds_received', label: 'Funds Received', order: 4 },
  { key: 'released', label: 'Released', order: 5 },
  { key: 'settled', label: 'Settled', order: 6 },
  { key: 'csd_payment_confirmed', label: 'CSD Confirmed', order: 7 },
]);

// ─── Alert Severities ───────────────────────────────────────────────────
export const ALERT_SEVERITIES = Object.freeze({
  INFO: 'info',
  WARNING: 'warning',
  CRITICAL: 'critical',
});

export const ALERT_SEVERITY_LABELS = Object.freeze({
  [ALERT_SEVERITIES.INFO]: 'Info',
  [ALERT_SEVERITIES.WARNING]: 'Warning',
  [ALERT_SEVERITIES.CRITICAL]: 'Critical',
});

// ─── Alert Types ────────────────────────────────────────────────────────
export const ALERT_TYPES = Object.freeze({
  DEADLINE_APPROACHING: 'deadline_approaching',
  QUOTE_EXPIRY: 'quote_expiry',
  LIMIT_BREACH: 'limit_breach',
  KYC_STATUS_CHANGE: 'kyc_status_change',
  LATE_FUNDS: 'late_funds',
  SSI_CHANGE: 'ssi_change',
  STALE_FEED: 'stale_feed',
  CB_REFERENCE_PUBLISHED: 'cb_reference_published',
});

// ─── Margin Band Defaults ───────────────────────────────────────────────
export const MARGIN_BANDS = Object.freeze({
  SMALL: { maxAmount: 500000, minMargin: 0.0005, maxMargin: 0.0030 },
  MEDIUM: { maxAmount: 2000000, minMargin: 0.0003, maxMargin: 0.0020 },
  LARGE: { maxAmount: Infinity, minMargin: 0.0001, maxMargin: 0.0015 },
});

// ─── Timing Constants ───────────────────────────────────────────────────
export const STALE_FEED_THRESHOLD_MS = 10000; // 10s before feed is considered stale
export const QUOTE_EXPIRY_WARNING_MS = 30000; // 30s warning before quote expires
export const DEADLINE_WARNING_MS = 120000; // 2min warning before RFQ deadline
export const PRICE_TICK_INTERVAL_MS = 2000; // Base interval for price ticks
